import React, {
  useState,
} from 'react'
import {
  useDispatch,
  useSelector,
} from 'react-redux'
import { 
  useNavigate, 
  Link as RouterLink
} from 'react-router-dom'

import { 
  LockOutlined as LockOutlinedIcon 
} from '@mui/icons-material'

import { 
  Avatar,
  Box,
  Button,
  Grid,
  Link,
  TextField,
  Typography,
 } from '@mui/material'

import {
  clearFlashAlert,
  clearFlashNotice,
  setFlashAlert,
  setFlashNotice,
} from '../ui/uiSlice'

import {
  selectCurrentUser,
  selectAuthToken,
  loadUserData,
} from './userSlice'

import { formatDeviseErrors } from '../../../components/devise/deviseErrors'
import { deviseFetch, showFailedRequest } from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'

const classes = {
  growBox: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  link: {
    margin: '0 5px 0 5px',
    cursor: 'pointer',
  }
}

export default function EditEmail() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const authToken = useSelector(selectAuthToken)
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()

  const handleLinkClick = (e) => {
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())
  }

  const handleSubmit = (evt) => {
    // Prevent default form submission and submit via xhr
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)
    for (let pair of data.entries()){
      console.log(pair[0], pair[1])
    }

    submit(async () => {
      const result = await deviseFetch(dispatch, "/user.json", {
        method: 'PUT',
        body: data,
        headers: {
          'X-CSRF-Token': authToken,
        },
        credentials: 'same-origin',
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.ok){
        console.log('updated email')
        // The address isn't changed until the user follows the link in the
        // confirmation email sent to it. Load the updated user so /app/account
        // shows the pending change straight away.
        if (json_data?.user) {
          dispatch(loadUserData(json_data))
        }
        dispatch(setFlashNotice(
          'Check your new email address for a link to confirm it. ' +
          'Your email will not change until you do.'
        ))
        navigate('/app/account')
        return
      }
      else if (response.status === 422){
        console.log('could not update')

        console.log(json_data)
        setErrors(json_data?.errors ?? {})

        const errorCount = Object.keys(json_data?.errors ?? {}).flat().length
        if (errorCount > 0){
          const alertMessage = `${
            errorCount > 1
            ? errorCount + ' errors'
            : 'One error'
            } prevented the email from being updated`

          dispatch(setFlashAlert(alertMessage))
        }

        return
      }

      // e.g. a 401 when the session has expired: show what the server said.
      dispatch(setFlashAlert(formatDeviseErrors(json_data)))
    })
  }

  return (
    <React.Fragment>
      <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
        <LockOutlinedIcon />
      </Avatar>
      <Typography component='h1' variant='h5'>
        Change email
      </Typography>
      <Box
        component="form"
        sx={{ mt: 1, width: '100%', maxWidth: 400 }}
        onSubmit={handleSubmit}
        noValidate  
      >
        <TextField
          margin="normal"
          required
          fullWidth
          label="Your password"
          id="user[current_password]"
          name="user[current_password]"
          type="password"
          autoComplete="password"
          error={Boolean(errors.current_password)}
          helperText={errors?.current_password?.map(
            e => e.charAt(0).toUpperCase() + e.slice(1)
          )?.join(', ')}
          autoFocus
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label="New email"
          id="user[email]"
          name="user[email]"
          type="email"
          autoComplete="email"
          error={Boolean(errors?.email)}
          helperText={errors?.email?.map(
            e => e.charAt(0).toUpperCase() + e.slice(1)
          )?.join(', ')}
        />      
        <Button
          type="submit"
          fullWidth
          variant="contained"
          loading={submitting}
          sx={{ mt: 3, mb: 2 }}
        >
          Change Email
        </Button>
        <Grid container>
          <Grid sx={{ flexGrow: 1 }}>
            <Link
              component={RouterLink}
              onClick={handleLinkClick}
              to='/app/account/edit_email'
              sx={classes.link}
              rel='nofollow'
              variant='body2'
            >
              Edit email
            </Link>
          </Grid>
          <Grid>
            <Link 
              component={RouterLink}
              onClick={handleLinkClick}
              to='/app/account/edit_password'
              sx={classes.link}
              rel='nofollow'
              variant='body2'
            >
              {"Change password"}
            </Link>
          </Grid>
        </Grid>           
      </Box>
    </React.Fragment>
  )
}

